// Calendar days of the tenant's time zone as UTC instants. Recordings are
// stored in UTC; operators pick "the 29th" in their own zone, including days
// of 23 or 25 hours around daylight-saving changes.

const partsCache = new Map<string, Intl.DateTimeFormat>();

function partsFormat(timeZone: string | undefined): Intl.DateTimeFormat {
  const key = timeZone ?? '';
  let f = partsCache.get(key);
  if (!f) {
    const opts: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    };
    try {
      f = new Intl.DateTimeFormat('en-US', { ...opts, ...(timeZone ? { timeZone } : {}) });
    } catch {
      f = new Intl.DateTimeFormat('en-US', opts); // unknown zone: viewer's zone
    }
    partsCache.set(key, f);
  }
  return f;
}

interface Wall {
  y: number;
  mo: number;
  d: number;
  h: number;
  mi: number;
  s: number;
}

function wall(t: number, timeZone: string | undefined): Wall {
  const out: Record<string, number> = {};
  for (const p of partsFormat(timeZone).formatToParts(new Date(t))) {
    if (p.type !== 'literal') out[p.type] = Number(p.value);
  }
  return {
    y: out.year ?? 1970,
    mo: out.month ?? 1,
    d: out.day ?? 1,
    h: out.hour ?? 0,
    mi: out.minute ?? 0,
    s: out.second ?? 0,
  };
}

/** Wall-clock time minus UTC, in ms, at instant t. */
function offsetAt(t: number, timeZone: string | undefined): number {
  const w = wall(t, timeZone);
  return Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi, w.s) - Math.floor(t / 1000) * 1000;
}

const dayRe = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isDay(s: string): boolean {
  return dayRe.test(s);
}

/** UTC instant of 00:00 of day (YYYY-MM-DD) in timeZone. */
export function dayStart(day: string, timeZone: string | undefined): number {
  const m = dayRe.exec(day);
  if (!m) throw new Error(`invalid day ${day}`);
  const midnight = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  // Two passes settle the offset across a daylight-saving change.
  let t = midnight - offsetAt(midnight, timeZone);
  t = midnight - offsetAt(t, timeZone);
  return t;
}

/** The day (YYYY-MM-DD) containing instant t in timeZone. */
export function dayOf(t: number, timeZone: string | undefined): string {
  const w = wall(t, timeZone);
  return `${String(w.y)}-${String(w.mo).padStart(2, '0')}-${String(w.d).padStart(2, '0')}`;
}

/** Calendar arithmetic on YYYY-MM-DD, independent of any zone. */
export function addDays(day: string, n: number): string {
  const m = dayRe.exec(day);
  if (!m) throw new Error(`invalid day ${day}`);
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + n));
  return d.toISOString().slice(0, 10);
}

/** [start, end) of a day; 23 or 25 hours around daylight-saving changes. */
export function dayRange(day: string, timeZone: string | undefined): [number, number] {
  return [dayStart(day, timeZone), dayStart(addDays(day, 1), timeZone)];
}

/**
 * UTC instant of wall-clock time "HH:MM" or "HH:MM:SS" on day in timeZone, or
 * null when the text is not a time.
 */
export function atWallClock(
  day: string,
  time: string,
  timeZone: string | undefined,
): number | null {
  const d = dayRe.exec(day);
  const m = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(time);
  if (!d || !m) return null;
  const [h, mi, s] = [Number(m[1]), Number(m[2]), Number(m[3] ?? 0)];
  if (h > 23 || mi > 59 || s > 59) return null;
  const naive = Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), h, mi, s);
  let t = naive - offsetAt(naive, timeZone);
  t = naive - offsetAt(t, timeZone);
  return t;
}

/** HH:MM:SS of instant t in timeZone. */
export function clock(t: number, timeZone: string | undefined): string {
  const w = wall(t, timeZone);
  return [w.h, w.mi, w.s].map((v) => String(v).padStart(2, '0')).join(':');
}
